import { context } from './launchSound.js';

/**
 * The music underneath the boot loader journey.
 *
 * A bed, not a tune. Anything with a melody competes with the narrator
 * for the same part of a learner's attention, and the narrator has to
 * win - so this is three quiet detuned voices, a slow filter drift, and
 * a soft pulse to keep it moving. It changes chord with the mood of the
 * section and does nothing else.
 *
 * Two things keep it out of the way. It sits about a fifth as loud as
 * the narration, and it ducks further still whenever the narrator is
 * actually speaking.
 *
 * Synthesised for the same reasons as the effects: nothing to download,
 * nothing to licence, and the production CSP allows no external media.
 */

const BED = 0.05;         // barely there, on purpose
const DUCKED = 0.018;     // and less than that under the voice

/*
 * One chord per mood, as frequencies in hertz.
 *
 *   intro    open and hopeful, for the power coming on
 *   calm     the same root, quieter and more settled, for explaining
 *   tense    a minor second in the middle, which is what makes the
 *            cybersecurity section feel wrong without being dramatic
 *   resolve  a major chord with the octave on top, for the payoff
 */
const MOODS = {
  intro:   { notes: [110, 164.81, 220], cutoff: 900,  rate: 0.5 },
  calm:    { notes: [110, 146.83, 220], cutoff: 650,  rate: 0.34 },
  tense:   { notes: [103.83, 110, 207.65], cutoff: 520, rate: 0.8 },
  resolve: { notes: [130.81, 196, 261.63], cutoff: 1100, rate: 0.45 },
};

let nodes = null;
let ducked = false;

export function start(mood = 'calm') {
  const ctx = context();
  if (!ctx || nodes) return;

  const out = ctx.createGain();
  out.gain.value = 0;
  out.connect(ctx.destination);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = MOODS[mood].cutoff;
  filter.Q.value = 2;
  filter.connect(out);

  // three voices, detuned a hair so the chord breathes rather than sits
  const voices = MOODS[mood].notes.map((hz, i) => {
    const osc = ctx.createOscillator();
    osc.type = i === 2 ? 'triangle' : 'sawtooth';
    osc.frequency.value = hz;
    osc.detune.value = (i - 1) * 6;
    const g = ctx.createGain();
    g.gain.value = i === 2 ? 0.25 : 0.4;
    osc.connect(g).connect(filter);
    osc.start();
    return { osc, gain: g };
  });

  // a slow sweep on the filter, which is most of what stops it being
  // a flat drone
  const lfo = ctx.createOscillator();
  lfo.frequency.value = MOODS[mood].rate * 0.12;
  const depth = ctx.createGain();
  depth.gain.value = MOODS[mood].cutoff * 0.35;
  lfo.connect(depth).connect(filter.frequency);
  lfo.start();

  // a soft pulse, so it has a heartbeat
  const pulse = ctx.createOscillator();
  pulse.frequency.value = MOODS[mood].rate;
  const pulseDepth = ctx.createGain();
  pulseDepth.gain.value = 0.35;
  pulse.connect(pulseDepth).connect(out.gain);
  pulse.start();

  nodes = { ctx, out, filter, voices, lfo, depth, pulse, pulseDepth, mood };
  fade(BED, 1.6);
}

function fade(to, seconds) {
  if (!nodes) return;
  const { ctx, out } = nodes;
  out.gain.cancelScheduledValues(ctx.currentTime);
  out.gain.setValueAtTime(Math.max(out.gain.value, 0.0001), ctx.currentTime);
  out.gain.linearRampToValueAtTime(to, ctx.currentTime + seconds);
}

/** Moves the bed to a new mood, over a few seconds rather than a cut. */
export function setMood(mood) {
  if (!nodes || !MOODS[mood] || nodes.mood === mood) return;
  const { ctx, voices, filter, depth, lfo, pulse } = nodes;
  const m = MOODS[mood];
  const t = ctx.currentTime;
  const glide = 2.5;

  voices.forEach((v, i) => {
    v.osc.frequency.cancelScheduledValues(t);
    v.osc.frequency.setValueAtTime(v.osc.frequency.value, t);
    v.osc.frequency.exponentialRampToValueAtTime(m.notes[i], t + glide);
  });
  filter.frequency.cancelScheduledValues(t);
  filter.frequency.setValueAtTime(filter.frequency.value, t);
  filter.frequency.linearRampToValueAtTime(m.cutoff, t + glide);
  depth.gain.linearRampToValueAtTime(m.cutoff * 0.35, t + glide);
  lfo.frequency.linearRampToValueAtTime(m.rate * 0.12, t + glide);
  pulse.frequency.linearRampToValueAtTime(m.rate, t + glide);

  nodes.mood = mood;
}

/** Pull the bed down under the narrator, and let it back up after. */
export function duck(on) {
  if (!nodes || ducked === on) return;
  ducked = on;
  fade(on ? DUCKED : BED, on ? 0.25 : 0.8);
}

export function stop() {
  if (!nodes) return;
  const { ctx, out, voices, lfo, pulse } = nodes;
  fade(0.0001, 0.5);
  const at = ctx.currentTime + 0.6;
  voices.forEach((v) => v.osc.stop(at));
  lfo.stop(at);
  pulse.stop(at);
  setTimeout(() => { try { out.disconnect(); } catch { /* already gone */ } }, 800);
  nodes = null;
  ducked = false;
}

export function playing() {
  return Boolean(nodes);
}
